/**
 * Small inline-editable inputs for the Quote Details gear editor, extracted
 * from GearListSection.tsx (#81). Each is a self-contained component with its
 * own buffer state and no dependency on the editor's closure — they talk to
 * the parent only through props.
 */
import { useEffect, useRef, useState } from "react";
import { cx } from "@shared/cx";
import cell from "@/components/ui/cell-input.module.css";
import styles from "./gear-list-inputs.module.css";
import { useDebouncedValue } from "../../lib/use-debounced-value";
import { useDismissable } from "../../lib/use-dismissable";
import { useListboxNav } from "../../lib/use-listbox-nav";
import {
  searchEquipment,
  type EquipmentSearchResult,
} from "../../lib/event-workspace-api";

// Autocomplete input for equipment search.
export function EquipmentAutocomplete({
  value,
  onSelect,
  placeholder,
  invalid,
  ariaLabel,
}: {
  value: string;
  onSelect: (item: EquipmentSearchResult | null, name: string) => void;
  placeholder?: string;
  invalid?: boolean;
  ariaLabel?: string;
}) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<EquipmentSearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 200);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    searchEquipment(debouncedQuery)
      .then((data) => {
        if (!cancelled) setResults(data);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  useDismissable({
    open: showDropdown,
    onDismiss: () => setShowDropdown(false),
    pointer: [wrapperRef],
  });

  const pick = (item: EquipmentSearchResult) => {
    setQuery(item.name);
    setShowDropdown(false);
    onSelect(item, item.name);
  };

  const nav = useListboxNav({
    open: showDropdown && results.length > 0,
    count: results.length,
    onCommit: (i) => pick(results[i]),
  });

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <input
        type="text"
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => {
          if (results.length > 0) setShowDropdown(true);
        }}
        onBlur={() => {
          setTimeout(() => {
            if (query !== value) {
              onSelect(null, query);
            }
          }, 200);
        }}
        onKeyDown={nav.onKeyDown}
        {...nav.comboboxProps}
        placeholder={placeholder}
        className={cx(cell.cell, invalid && cell.invalid)}
      />
      {showDropdown && results.length > 0 && (
        <div
          id={nav.listboxId}
          role="listbox"
          aria-label={
            ariaLabel ? `${ariaLabel} suggestions` : "Equipment suggestions"
          }
          className={styles.menu}
        >
          {results.map((item, i) => (
            <button
              key={item.id}
              id={nav.optionId(i)}
              type="button"
              role="option"
              aria-selected={i === nav.activeIndex}
              tabIndex={-1}
              className={cx(
                styles.option,
                i === nav.activeIndex && styles.optionActive,
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(item);
              }}
            >
              <span>{item.name}</span>
              <span className={styles.optionRate}>
                ${item.hire_rate_per_day}/day
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Inline-editable package name on the active tab (#20). Buffers locally and
// commits on blur/Enter, mirroring GroupHeadingInput.
export function PackageNameInput({
  value,
  onRename,
}: {
  value: string;
  onRename: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  return (
    <input
      type="text"
      aria-label="Package name"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim();
        if (next && next !== value) {
          onRename(next);
        } else {
          setDraft(value);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      size={Math.max(6, draft.length)}
      className={cell.cellName}
    />
  );
}

// Inline-editable group heading. Edits buffer locally and commit on
// blur/Enter so a half-typed name never merges into another group mid-stroke.
export function GroupHeadingInput({
  value,
  onRename,
}: {
  value: string;
  onRename: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  return (
    <input
      type="text"
      aria-label="Group heading"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim();
        if (next && next !== value) {
          onRename(next);
        } else {
          setDraft(value);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      className={cell.cellHeading}
    />
  );
}
